// React

import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";
import { UserContext } from "context/user";


// @mui material components
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tooltip,
} from "@mui/material";

import ModalDelete from "components/Modals/Delete";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { validateCUIL, validateDNI } from "utils/validations";
import { getUproColor } from "utils/colors";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "70%", xxl: "50%" },
  overflow: "auto",
  p: 3,
};

const ADD_PROVEEDOR = gql`
  mutation addProveedor($input: ProveedorInput!) {
    saveProveedor(input: $input) {
      id
    }
  }
`;

const DELETE_PROVEEDOR = gql`
  mutation deleteProveedor($id: ID!) {
    deleteProveedor(id: $id) {
      id
    }
  }
`;

export default function ModalAddProveedor({ open, handleClose, data, refetch, readOnly = false }) {
  const [proveedor, setProveedor] = useState();
  const [isEdit, setIsEdit] = useState();
  const { handleSnackbar } = useContext(MessageManager);
  const [errors, setErrors] = useState({});
  const { verificarAcceso } = useContext(UserContext);


  const [addProveedor, { loading: loadingSave }] = useMutation(ADD_PROVEEDOR);

  const { data: provincias } = useQuery(gql`
    query GetProvincias {
      provincias {
        id
        nombre
      }
    }
  `);

  const [getLocalidades, { data: localidades }] = useLazyQuery(gql`
    query GetLocalidades($provincia: ID!) {
      localidades(provincia: $provincia) {
        id
        nombre
        provincia {
          id
        }
      }
    }
  `);

  const { data: rubros } = useQuery(gql`
    query GetRubros {
      categoriasMateriaPrima(order: { field: "nombre", order: ASC }) {
        id
        nombre
      }
    }  
  `);

  const handleSave = () => {
    addProveedor({
      variables: {
        input: {
          id: proveedor.id || null,
          tipoProveedor: proveedor.tipoProveedor,
          nombre: proveedor.nombre,
          apellido: proveedor.apellido,
          tipoDocumento: proveedor.tipoDocumento,
          documento: proveedor.documento,
          tipoResponsable: proveedor.tipoResponsable,
          razonSocial: proveedor.razonSocial || null,
          idProvincia: proveedor.provincia,
          idLocalidad: proveedor.localidad,
          idsCategorias: proveedor?.idsCategorias.sort((a, b) => a - b) || [],
          telefono: proveedor.telefono,
          telefonoSecundario: proveedor.telefonoSecundario || null,
          referenciaContacto: proveedor.referenciaContacto || null,
        },
      },
    })
      .then(() => {
        setProveedor();
        handleSnackbar("Proveedor guardado correctamente", "success");
        handleClose();
        refetch();
      })
      .catch(() => {
        handleSnackbar("Error al agregar el proveedor", "error");
      });
  };

  const validate = (name, value) => {
    let error = null;
    switch (name) {
      case "tipoProveedor":
        if (value === -1) {
          error = {
            message: "Requerido",
            visible: false,
          };
        };
        if (value === "Jurídica" && !proveedor?.razonSocial) {
          error = {
            message: "Debe cargar la razón social",
            visible: true,
          };
        };
        if (value === "Jurídica" && proveedor?.tipoDocumento !== 80) {
          error = {
            message: "Las personas Jurídicas deben cargar CUIT",
            visible: true,
          };
        };
        break;
      case "nombre":
        if (!value.trim()) {
          error = {
            message: "Requerido",
            visible: false,
          };
        }
        break;
      case "apellido":
        if (!value.trim()) {
          error = {
            message: "Requerido",
            visible: false,
          };
        }
        break;
      case "tipoDocumento":
        if (value === -1) {
          error = {
            message: "Requerido",
            visible: false,
          };
        }
        break;
      case "documento":
        if (!value.trim()) {
          error = {
            message: "Requerido",
            visible: false,
          };
        } else if (proveedor?.tipoDocumento === 96 && !validateDNI(value)) {
          error = {
            message: "DNI inválido",
            visible: true,
          };
        } else if (proveedor?.tipoDocumento != 96 && !validateCUIL(value)) {
          error = {
            message: "CUIL/CUIT inválido",
            visible: true,
          };
        }
        break;
      case "tipoResponsable":
        if (value === -1) {
          error = {
            message: "Requerido",
            visible: false,
          };
        }
        break;
      case "provincia":
        if (value === -1) {
          error = {
            message: "Requerido",
            visible: false,
          };
        }
        break;
      case "localidad":
        if (value === -1) {
          error = {
            message: "Requerido",
            visible: false,
          };
        }
        break;
      // case "idsCategorias":
      //   if (value.length === 0) {
      //     error = {
      //       message: "Requerido",
      //       visible: false,
      //     };
      //  }
      //  break;
      case "telefono":
        if (!value || !value.trim()) {
          error = {
            message: "Requerido",
            visible: false,
          };
        } else if (!/^\d{6,15}$/.test(value.trim())) {
          error = {
            message: "Teléfono inválido (solo números, entre 6 y 15 dígitos)",
            visible: true,
          };
        }
        break;      
      default:
        break;
    }
    if (error) {
      setErrors((prev) => ({ ...prev, [name]: error }));
    } else if (errors[name]) {
      let err = { ...errors };
      delete err[name];
      setErrors(err);
    }
  };

  const handleChanges = (e) => {
    const { name, value } = e.target;
    validate(name, value);
    setProveedor({
      ...proveedor,
      [name]: value,
    });
  };

  useEffect(() => {
    if (proveedor?.tipoDocumento) {
      validate("documento", proveedor.documento);
      validate("tipoProveedor", proveedor.tipoProveedor);
    }
  }, [proveedor?.tipoDocumento]);

  useEffect(() => {
    if (proveedor?.razonSocial) {
      validate("tipoProveedor", proveedor.tipoProveedor);
    }
  }, [proveedor?.razonSocial]);

  useEffect(() => {
    if (open && data) {
      setProveedor(data);
      setIsEdit(Boolean(data?.id));
      Object.keys(data).forEach((key) => {
        validate(key, data[key]);
      });
    } else {
      setErrors({});
    }
  }, [open, data]);

  useEffect(() => {
    if (proveedor?.provincia) {
      getLocalidades({ variables: { provincia: proveedor?.provincia } }).then((data) => {
        // Si la localidad no pertenece a la provincia seleccionada, se limpia el campo
        if (!data?.data?.localidades?.find((localidad) => localidad.id === proveedor?.localidad)) {
          setProveedor({
            ...proveedor,
            localidad: -1,
          });
        }
      });
    }
  }, [proveedor?.provincia]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" mb={2}>
              <SoftBox>
                <SoftTypography variant="h6">
                  {isEdit ? "Editar proveedor" : "Agregar proveedor"}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <InputLabel htmlFor="tipoProveedor">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Tipo de proveedor
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="tipoProveedor"
                  value={proveedor?.tipoProveedor || -1}
                  onChange={handleChanges}
                >
                  <MenuItem value={-1} disabled>
                    Seleccione uno
                  </MenuItem>
                  <MenuItem value="Física">Persona física</MenuItem>
                  <MenuItem value="Jurídica">Persona Jurídica</MenuItem>
                </Select>
              </Grid>
              {proveedor?.tipoProveedor === "Jurídica" && (
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="razonSocial">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Razon Social
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="razonSocial"
                    placeholder="Inserte la razón social"
                    type="text"
                    name="razonSocial"
                    value={proveedor?.razonSocial || ""}
                    onChange={handleChanges}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <InputLabel htmlFor="nombre">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Nombre
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  label="nombre"
                  placeholder="Inserte el nombre del proveedor"
                  type="text"
                  name="nombre"
                  value={proveedor?.nombre || ""}
                  onChange={handleChanges}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InputLabel htmlFor="apellido">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Apellido
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  label="apellido"
                  placeholder="Inserte el apellido del proveedor"
                  type="text"
                  name="apellido"
                  value={proveedor?.apellido || ""}
                  onChange={handleChanges}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InputLabel htmlFor="tipoDocumento">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Tipo de documento
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="tipoDocumento"
                  value={proveedor?.tipoDocumento || -1}
                  onChange={handleChanges}
                >
                  <MenuItem value={-1} disabled>
                    Seleccione uno
                  </MenuItem>

                  <MenuItem value={96}>DNI</MenuItem>
                  <MenuItem value={80}>CUIT</MenuItem>
                  <MenuItem value={86}>CUIL</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} sm={4}>
                <InputLabel htmlFor="documento">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Documento
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  label="documento"
                  placeholder="Inserte el documento del proveedor"
                  type="text"
                  name="documento"
                  value={proveedor?.documento || ""}
                  error={
                    proveedor?.tipoDocumento === 96 && proveedor?.documento?.length > 0
                      ? !validateDNI(proveedor?.documento)
                      : proveedor?.documento?.length > 0 && !validateCUIL(proveedor?.documento)
                  }
                  onChange={handleChanges}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InputLabel htmlFor="tipoResponsable">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Tipo de responsable
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="tipoResponsable"
                  value={proveedor?.tipoResponsable || -1}
                  onChange={handleChanges}
                >
                  <MenuItem value={-1} disabled>
                    Seleccione uno
                  </MenuItem>
                  <MenuItem value={1}>IVA Responsable Inscripto</MenuItem>
                  <MenuItem value={2}>IVA Responsable no Inscripto</MenuItem>
                  <MenuItem value={3}>IVA no Responsable</MenuItem>
                  <MenuItem value={4}>IVA Sujeto Exento</MenuItem>
                  <MenuItem value={5}>Consumidor Final</MenuItem>
                  <MenuItem value={6}>Responsable Monotributo</MenuItem>
                  <MenuItem value={7}>Sujeto no Categorizado</MenuItem>
                  <MenuItem value={8}>Proveedor del Exterior</MenuItem>
                  <MenuItem value={9}>Cliente del Exterior</MenuItem>
                  <MenuItem value={10}>IVA Liberado – Ley Nº 19.640</MenuItem>
                  <MenuItem value={11}>IVA Responsable Inscripto – Agente de Percepción</MenuItem>
                  <MenuItem value={12}>Pequeño Contribuyente Eventual</MenuItem>
                  <MenuItem value={13}>Monotributista Social</MenuItem>
                  <MenuItem value={14}>Pequeño Contribuyente Eventual Social</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} sm={proveedor?.tipoProveedor === "Jurídica" ? 4 : 6}>
                <InputLabel htmlFor="provincia">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Provincia
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="provincia"
                  value={proveedor?.provincia || -1}
                  onChange={handleChanges}
                >
                  <MenuItem value={-1} disabled>
                    Seleccione uno
                  </MenuItem>
                  {provincias?.provincias?.map((provincia) => (
                    <MenuItem key={provincia.id} value={provincia.id}>
                      {provincia.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} sm={proveedor?.tipoProveedor === "Jurídica" ? 4 : 6}>
                <InputLabel htmlFor="localidad">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Localidad
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="localidad"
                  value={proveedor?.localidad || -1}
                  onChange={handleChanges}
                >
                  <MenuItem value={-1} disabled>
                    Seleccione uno
                  </MenuItem>
                  {localidades?.localidades?.map((localidad) => (
                    <MenuItem key={localidad.id} value={localidad.id}>
                      {localidad.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} sm={4}>
                <InputLabel htmlFor="telefono">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Teléfonos
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*&nbsp;
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  label="telefono"
                  placeholder="Teléfono 1 Obligatorio"
                  type="text"
                  name="telefono"
                  value={proveedor?.telefono || ""}
                  onChange={handleChanges}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InputLabel htmlFor="telefonoSecundario">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    &nbsp;
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  label="telefonoSecundario"
                  placeholder="Teléfono 2"
                  type="text"
                  name="telefonoSecundario"
                  value={proveedor?.telefonoSecundario || ""}
                  onChange={handleChanges}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InputLabel htmlFor="referenciaContacto">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Referencia de contacto
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  label="referenciaContacto"
                  placeholder="Nombre de la persona de contacto"
                  type="text"
                  name="referenciaContacto"
                  value={proveedor?.referenciaContacto || ""}
                  onChange={handleChanges}
                />
              </Grid>
              <Grid item xs={12}>
                <InputLabel htmlFor="usuario">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Rubros de interés
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*&nbsp;
                    </SoftTypography>
                    <Tooltip
                      placement="top"
                      title={
                        <>
                          <SoftTypography variant="caption" display="block" color="#000000">
                            Los rubros de interés serán utilizados para avisar de cotizaciones abiertas que se adapten a su perfil.
                          </SoftTypography>
                          <SoftTypography variant="caption" display="block" sx={{ marginTop: 1 }} color="#000000">
                            En caso de detectar discrepancias en los rubros inscriptos, por favor comuníquese con el área de Administración del Comedor.
                          </SoftTypography>
                        </>
                      }
                    >
                      <Icon color="dark">help</Icon>
                    </Tooltip>
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="idsCategorias"
                  multiple
                  value={proveedor?.idsCategorias || []}
                  onChange={handleChanges}
                  disabled={!verificarAcceso(30)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={rubros?.categoriasMateriaPrima?.find((rubro) => rubro.id === value)?.nombre}
                          sx={{ backgroundColor: getUproColor("uproGreen"), color: "#ffffff" }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {rubros?.categoriasMateriaPrima?.map((rubro, index) => (
                    <MenuItem key={index} value={rubro.id}>
                      {rubro.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              {proveedor?.user?.email ? (
                <Grid item xs={12}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Email: &nbsp;
                  </SoftTypography>
                  <SoftTypography variant="caption">
                    {proveedor?.user?.email}
                  </SoftTypography >
                </Grid>
              ) : null}
            </Grid>
            <SoftBox display="flex" justifyContent="end" mt={2} gap={1} flexDirection={{ xs: "column", sm: "row" }} alignItems="center">
              {Object.values(errors).filter(e => e.visible).length > 0 && (
                <SoftBox display="flex" alignItems="center" color="error">
                  <Icon fontSize="medium">error</Icon>
                  <SoftTypography variant="caption" color="error">
                    {Object.values(errors).filter(e => e.visible)[0].message}
                  </SoftTypography>
                </SoftBox>
              )}
              <SoftButton
                color="uproGreen"
                circular
                onClick={handleClose}
                disabled={loadingSave}
              >
                <Icon sx={{ fontWeight: "light" }}>close</Icon>
                &nbsp;Cerrar
              </SoftButton>
              <SoftButton
                color="uproPink"
                circular
                disabled={
                  loadingSave ||
                  Object.keys(errors).length > 0 ||
                  readOnly
                }
                onClick={handleSave}
              >
                {loadingSave ? (
                  <CircularProgress size={15} color="white" />
                ) : (
                  <Icon sx={{ fontWeight: "light" }}>save</Icon>
                )}
                &nbsp;Guardar
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalAddProveedor.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  data: PropTypes.object,
  refetch: PropTypes.func,
  readOnly: PropTypes.bool,
};
